import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Intercom, {
  boot,
  shutdown,
  showNewMessage,
  update,
} from '@intercom/messenger-js-sdk'

import fetchFinJwt from '@/api/fin'
import { useAccounts } from '@/components/Providers/AccountsProvider'
import { getIntercomSettings } from '@/utils/intercom'
import { sendError } from '@/utils/rollbar'
import tokens, { getJWTFromStorage } from '@/utils/tokens'

const noop = () => undefined

export type IntercomContextValue = {
  isReady: boolean
  openChat: (message?: string) => void
  shutdown: () => void
}

const IntercomContext = createContext<IntercomContextValue>({
  isReady: false,
  openChat: noop,
  shutdown: noop,
})

export const useIntercom = () => useContext(IntercomContext)

const IntercomProvider = ({ children }: { children: ReactNode }) => {
  const { account } = useAccounts() as { account?: { id?: string } }
  const [isReady, setIsReady] = useState(false)
  const bootRequestIdRef = useRef(0)
  const didBootRef = useRef(false)
  const isBootingRef = useRef(false)
  const accountIdRef = useRef<string | undefined>(undefined)

  const shutdownIntercom = useCallback(() => {
    bootRequestIdRef.current += 1
    if (didBootRef.current) shutdown()
    didBootRef.current = false
    isBootingRef.current = false
    accountIdRef.current = undefined
    setIsReady(false)
  }, [])

  const load = useCallback(async (accountId?: string) => {
    if (!accountId) return
    if (
      accountIdRef.current === accountId
      && (didBootRef.current || isBootingRef.current)
    ) return

    if (didBootRef.current || isBootingRef.current) shutdownIntercom()

    const bootRequestId = bootRequestIdRef.current + 1
    bootRequestIdRef.current = bootRequestId
    accountIdRef.current = accountId
    isBootingRef.current = true
    setIsReady(false)

    try {
      const intercomUserJwt = await fetchFinJwt()
      if (bootRequestId !== bootRequestIdRef.current) return

      const settings = getIntercomSettings(intercomUserJwt)
      Intercom(settings)
      boot(settings)
      didBootRef.current = true
      setIsReady(true)
    } catch (err) {
      if (bootRequestId !== bootRequestIdRef.current) return
      accountIdRef.current = undefined
      sendError(err, 'Error fetching Fin JWT')
      setIsReady(false)
    } finally {
      if (bootRequestId === bootRequestIdRef.current) {
        isBootingRef.current = false
      }
    }
  }, [shutdownIntercom])

  const openChat = useCallback((message = '') => {
    if (!didBootRef.current) return
    update({ hide_default_launcher: false })
    showNewMessage(message)
  }, [])

  useEffect(() => {
    if (account?.id) load(account.id)
    else shutdownIntercom()
  }, [account?.id, load, shutdownIntercom])

  useEffect(() => {
    const handleTokenSync = () => {
      if (!getJWTFromStorage()) shutdownIntercom()
    }

    tokens.onSync(handleTokenSync)
    return () => {
      tokens.removeOnSync(handleTokenSync)
    }
  }, [shutdownIntercom])

  useEffect(() => () => shutdownIntercom(), [shutdownIntercom])

  const providerValue = useMemo(() => ({
    isReady,
    openChat,
    shutdown: shutdownIntercom,
  }), [isReady, openChat, shutdownIntercom])

  return (
    <IntercomContext.Provider value={providerValue}>
      {children}
    </IntercomContext.Provider>
  )
}

export default IntercomProvider
